import React, { useCallback, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import styled from 'styled-components';
import { MdSearch } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { fetchManufacturerRetailerVenues } from '../../../../services/Http/ManufacturerApi';
import { useCurrency } from '../../../../context/CurrencyProvider';

const RetailersVenues = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [records, setRecords] = useState([]);
    const [meta, setMeta] = useState({ total: 0, per_page: 10, current_page: 1 });
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    const loadVenues = useCallback(async ({ page = 1, perPage = 10, searchTerm = '' } = {}) => {
        setLoading(true);
        try {
            const response = await fetchManufacturerRetailerVenues({ page, perPage, search: searchTerm });
            setRecords(response.data || []);
            setMeta(response.meta || { total: 0, per_page: perPage, current_page: page });
        } catch (error) {
            toast.error(t('ManufacturerRetailers.venues.errors.loadList'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadVenues();
    }, [loadVenues]);

    const handleSearch = (event) => {
        event.preventDefault();
        loadVenues({ page: 1, perPage: meta.per_page, searchTerm: search });
    };

    const columns = [
        {
            name: t('ManufacturerRetailers.venues.columns.venue'),
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: t('ManufacturerRetailers.venues.columns.retailer'),
            selector: (row) => row.retailer,
            sortable: true,
        },
        {
            name: t('ManufacturerRetailers.venues.columns.location'),
            selector: (row) => `${row.city || ''}${row.country ? `, ${row.country}` : ''}`,
            sortable: true,
        },
        {
            name: t('ManufacturerRetailers.venues.columns.sellThroughValue'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.sell_through_value,
                localAmount: row.sell_through_value_local ?? row.sell_through_local,
                localCurrencyId: row.sell_through_currency_id ?? row.currency_id,
                localCurrencyCode: row.sell_through_currency_code ?? row.currency_code,
            }),
            sortable: true,
        },
        {
            name: t('ManufacturerRetailers.venues.columns.lastActivity'),
            selector: (row) => formatDate(row.last_transaction_at),
            sortable: true,
        },
    ];

    return (
        <>
            <Toolbar>
                <SearchForm onSubmit={handleSearch}>
                    <MdSearch />
                    <input
                        type="text"
                        placeholder={t('ManufacturerRetailers.venues.search')}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </SearchForm>
            </Toolbar>
            <DataTable
                columns={columns}
                data={records}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={meta.total}
                paginationPerPage={meta.per_page}
                onChangePage={(page) => loadVenues({ page, perPage: meta.per_page, searchTerm: search })}
                onChangeRowsPerPage={(perPage, page) => loadVenues({ page, perPage, searchTerm: search })}
                highlightOnHover
                persistTableHead
            />
        </>
    );
};

export default RetailersVenues;

const Toolbar = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
`;

const SearchForm = styled.form`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(130, 22, 146, 0.05);
    border: 1px solid rgba(130, 22, 146, 0.2);
    border-radius: 8px;
    padding: 0.375rem 0.75rem;

    input {
        border: none;
        background: transparent;
        outline: none;
        width: 220px;
    }

    svg {
        color: rgba(130, 22, 146, 1);
    }
`;

const formatDate = (value) => {
    if (!value) {
        return '—';
    }
    return new Date(value).toLocaleDateString();
};
